<template>
  <v-container fluid>
    <v-row>
      <global-EventFilterMenu
        header="Transaction Filter"
        showStallholders
        showTerminals
        transactionsMode
        @submit="handleSubmit"
        @cleared="handleCleared"
      />
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card dark outlined>
          <v-card-title class="secondary py-1 text-subtitle-1">
            <v-icon left dense> mdi-file-table </v-icon>
            Transactions
            <v-spacer />
            <v-btn
              v-if="continueSearchActive"
              small
              color="warning"
              class="mr-2"
              @click="cancelContinueSearch"
            >
              <v-icon left x-small>mdi-close</v-icon>
              Cancel loading
            </v-btn>
            <v-btn
              v-if="IsAdmin"
              small
              class="mr-1"
              :disabled="!selectedRow || continueSearchActive"
              @click="showRawTransaction"
            >
              Show Transaction Data
            </v-btn>
            <v-btn
              small
              class="mr-1"
              :disabled="csvExportBusy || continueSearchActive"
              :loading="csvExportBusy"
              @click="generateCSV"
            >
              Generate CSV Report
            </v-btn>
            <v-btn
              v-if="csvExportBusy"
              small
              text
              class="mr-1"
              @click="cancelCsvExport"
            >
              Cancel export
            </v-btn>
            <span
              v-if="csvExportBusy && csvExportProgress"
              class="text-caption grey--text text--lighten-1 mr-2"
            >
              {{ csvExportProgress }}
            </span>
            <v-btn
              small
              :disabled="!selectedRow || pdfBusy || continueSearchActive"
              :loading="pdfBusy"
              @click="createPDF"
            >
              Generate tax invoice
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-overlay :value="tableBusy" absolute>
              <div class="d-flex flex-column align-center">
                <v-progress-circular indeterminate size="48" class="mb-3" />
                <div class="text-body-2 text-center px-4 mb-3">
                  {{ tableLoadingText }}
                </div>
                <v-btn
                  v-if="continueSearchActive"
                  color="warning"
                  @click="cancelContinueSearch"
                >
                  <v-icon left>mdi-close</v-icon>
                  Cancel
                </v-btn>
              </div>
            </v-overlay>
            <v-data-table
              v-model="selectedTransaction"
              :headers="headers"
              :items="pagedTransactions"
              :server-items-length="serverItemsLength"
              :options.sync="tableOptions"
              :footer-props="tableFooterProps"
              item-key="TransactionId"
              show-select
              single-select
              class="elevation-5"
              no-data-text="No transactions — set filters and press Submit"
            >
              <template v-slot:header.DateCreated="{ header }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">{{ header.text }}</span>
                  </template>
                  <span
                    >Sorts by full date &amp; time (Timestamp), not the
                    displayed date alone</span
                  >
                </v-tooltip>
              </template>
              <template v-slot:header.TimeCreated="{ header }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">{{ header.text }}</span>
                  </template>
                  <span
                    >Sorts chronologically by full date &amp; time. Displayed
                    clock time can look out of order across days (e.g. 12am vs
                    4pm).</span
                  >
                </v-tooltip>
              </template>
              <template v-slot:item.TransactionId="{ item }">
                {{ formatTransactionId(item.TransactionId) }}
              </template>
              <template v-slot:item.TotalAmount="{ item }">
                {{ formatAmount(item.TotalAmount) }}
              </template>
            </v-data-table>
            <div
              v-if="formattedTransactions.length || continueSearchActive"
              class="d-flex align-center px-4 py-2"
            >
              <span class="text-caption grey--text text--lighten-1">
                <template v-if="continueSearchActive">
                  Loading all results… batch {{ continueSearchBatches }}
                  <template v-if="formattedTransactions.length">
                    — showing {{ formattedTransactions.length }} so far
                  </template>
                </template>
                <template v-else>
                  Showing {{ formattedTransactions.length }} transaction{{
                    formattedTransactions.length === 1 ? "" : "s"
                  }}{{
                    hasMoreTransactions
                      ? " — more on server (Submit to load all)"
                      : ""
                  }}
                </template>
              </span>
              <v-spacer />
              <v-btn
                v-if="continueSearchActive"
                text
                x-small
                @click="cancelContinueSearch"
              >
                Cancel
              </v-btn>
              <v-btn
                v-else-if="hasMoreTransactions && !isAllRowsMode"
                text
                x-small
                :disabled="tableBusy"
                @click="handleLoadMore({})"
              >
                Load more
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="rawDialog" max-width="800" dark scrollable>
      <v-card>
        <v-card-title>
          Transaction Data
          <v-spacer />
          <v-btn icon @click="rawDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <pre class="raw-json">{{ rawJsonText }}</pre>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="downloadRawTxt">Download TXT</v-btn>
          <v-btn color="primary" text @click="rawDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapState, mapMutations } from "vuex";
import "izitoast/dist/css/iziToast.min.css";
import iziToast from "izitoast/dist/js/iziToast.min.js";
import { dashboardAPI } from "@/services";
import { downloadFromUrl } from "@/utils/download";

export default {
  data: () => ({
    selectedTransaction: [],
    rawDialog: false,
    rawJsonText: "",
    pdfBusy: false,
    csvExportBusy: false,
    csvExportProgress: "",
    csvExportJobId: null,
    csvExportCancel: false,
    suppressPageWatch: false,
    continueSearchActive: false,
    continueSearchBatches: 0,
    continueSearchCancel: false,
    tableOptions: {
      page: 1,
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: [],
      groupBy: [],
      groupDesc: [],
      multiSort: false,
      mustSort: false,
    },
    // Include All (-1): selecting it loads every remaining batch (cancellable).
    tableFooterProps: {
      "items-per-page-options": [10, 25, 50, 100, 250, -1],
    },
    headers: [
      { text: "Transaction ID", value: "TransactionId" },
      { text: "Date", value: "DateCreated" },
      { text: "Time", value: "TimeCreated" },
      { text: "Stallholder", value: "StallholderName" },
      { text: "Stall", value: "StallName" },
      { text: "Terminal Number", value: "TerminalNumber" },
      { text: "Device ID", value: "DeviceId" },
      { text: "Status", value: "Status" },
      { text: "STAN", value: "Stan" },
      { text: "Order Number", value: "OrderNumber" },
      { text: "Type", value: "Type" },
      { text: "Amount", value: "TotalAmount" },
    ],
    // Aaron Example CSV output.xlsx column order / labels
    csvColumns: [
      { text: "Transaction ID", value: "TransactionId", format: "txnId" },
      { text: "Event", value: "EventName" },
      { text: "Stallholder", value: "StallholderName" },
      { text: "Stall", value: "StallName" },
      { text: "Terminal", value: "TerminalNumber" },
      { text: "User", value: "TerminalUserName" },
      { text: "Device ID", value: "DeviceId" },
      { text: "Type", value: "Type" },
      { text: "Status", value: "Status" },
      { text: "STAN", value: "Stan" },
      { text: "Order Number", value: "OrderNumber" },
      { text: "Amount", value: "TotalAmount", format: "money" },
      { text: "GST", value: "TotalGST", format: "money" },
      { text: "Cash Out", value: "TotalCashOutAmount", format: "money" },
      { text: "Card Surcharge", value: "CardSurchargeTotal", format: "money" },
      {
        text: "Card Surcharge Percentage",
        value: "CardSurchargePercentage",
      },
      {
        text: "Discount Surcharge",
        value: "ProductDiscountSurchargeTotal",
        format: "money",
      },
      {
        text: "Discount Surcharge Percentage",
        value: "ProductDiscountSurchargePercentage",
      },
      { text: "Voucher", value: "VoucherName" },
      { text: "Voided Items", value: "VoidedItems" },
      {
        text: "Product JSON",
        value: "TransactionItems",
        format: "json",
      },
    ],
  }),
  computed: {
    ...mapState("filter", [
      "globalEventFilter",
      "deviceId",
      "stan",
      "totalAmount",
      "transactionId",
      "searchStallholders",
      "searchStalls",
      "searchTerminals",
      "searchStartTime",
      "searchEndTime",
      "IsAdmin",
      "allTerminalUsers",
      "allVouchers",
      "allStalls",
      "allStallHolders",
    ]),
    ...mapState("dashboard", [
      "transactionsTransactions",
      "transactionsLoading",
      "transactionsLastEvaluatedKey",
    ]),
    hasMoreTransactions() {
      return !!this.transactionsLastEvaluatedKey;
    },
    tableBusy() {
      return this.transactionsLoading || this.continueSearchActive;
    },
    tableLoadingText() {
      if (this.continueSearchActive) {
        return `Loading all results… (batch ${this.continueSearchBatches}) — Cancel to stop`;
      }
      return "Searching transactions…";
    },
    isAllRowsMode() {
      return (this.tableOptions && this.tableOptions.itemsPerPage) === -1;
    },
    tableItemsPerPage() {
      const per = this.tableOptions && this.tableOptions.itemsPerPage;
      if (per === -1) return null;
      return per || 10;
    },
    /** Pretend one extra page exists while the API still has a cursor. */
    serverItemsLength() {
      const n = this.formattedTransactions.length;
      // All = show every loaded row only (no server fetch from this control)
      if (this.isAllRowsMode) return n;
      if (!this.hasMoreTransactions) return n;
      const per = this.tableItemsPerPage;
      const pages = Math.max(1, Math.ceil(n / per));
      return pages * per + 1;
    },
    pagedTransactions() {
      const rows = this.sortedTransactions;
      const per = this.tableItemsPerPage;
      // All: show every batch already in memory
      if (!per) return rows;
      const page = (this.tableOptions && this.tableOptions.page) || 1;
      const start = (page - 1) * per;
      return rows.slice(start, start + per);
    },
    sortedTransactions() {
      const source = this.formattedTransactions;
      const sortBy = (this.tableOptions && this.tableOptions.sortBy) || [];
      const sortDesc = (this.tableOptions && this.tableOptions.sortDesc) || [];
      if (!sortBy.length || !source.length) return source;

      const key = sortBy[0];
      const desc = !!sortDesc[0];

      const sortValue = (row) => {
        if (key === "TimeCreated" || key === "DateCreated") {
          return (
            row.Timestamp ||
            row.DateTime ||
            `${row.DateCreated || ""} ${row.TimeCreated || ""}`.trim() ||
            ""
          );
        }
        if (key === "TotalAmount") {
          const n = Number(
            String(row.TotalAmount ?? "").replace(/[^0-9.-]/g, "")
          );
          return Number.isNaN(n) ? 0 : n;
        }
        const v = row[key];
        return v == null ? "" : v;
      };

      const rows = source.slice();
      rows.sort((a, b) => {
        const av = sortValue(a);
        const bv = sortValue(b);
        let cmp = 0;
        if (typeof av === "number" && typeof bv === "number") {
          cmp = av - bv;
        } else {
          cmp = String(av).localeCompare(String(bv), undefined, {
            numeric: true,
            sensitivity: "base",
          });
        }
        return desc ? -cmp : cmp;
      });
      return rows;
    },
    formattedTransactions() {
      if (
        !this.transactionsTransactions ||
        this.transactionsTransactions.length === 0
      ) {
        return [];
      }
      return this.transactionsTransactions.map((transaction) => ({
        ...transaction,
        Type: transaction.Type || transaction.TransactionType || "",
        StallholderName:
          transaction.StallholderName || transaction.StallHolderName || "",
        TransactionId:
          transaction.TransactionId || transaction.TransactionID || "",
        EventName:
          transaction.EventName || this.globalEventFilter.EventName || "",
        TerminalUserName: this.lookupTerminalUserName(transaction.StallUserId),
        VoucherName: this.lookupVoucherName(transaction),
        TotalCashOutAmount:
          transaction.TotalCashOutAmount ?? transaction.CashOutAmount ?? "",
        TotalGST: transaction.TotalGST ?? transaction.GstTotal ?? "",
      }));
    },
    selectedRow() {
      return this.selectedTransaction && this.selectedTransaction[0]
        ? this.selectedTransaction[0]
        : null;
    },
  },
  watch: {
    "globalEventFilter.EventId"(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.clearResults();
      }
    },
    "tableOptions.page"() {
      this.onTablePageChange();
    },
    "tableOptions.itemsPerPage"(val) {
      // All only changes how loaded rows are displayed — fetch happens on Submit
      if (val === -1) {
        this.setTablePage(1);
        return;
      }
      this.onTablePageChange();
    },
    // Reset to page 1 on sort — but never rewrite tableOptions when already
    // on page 1 (that + options.sync caused an infinite update loop / freeze).
    "tableOptions.sortBy"() {
      if ((this.tableOptions && this.tableOptions.page) !== 1) {
        this.setTablePage(1);
      }
    },
    "tableOptions.sortDesc"() {
      if ((this.tableOptions && this.tableOptions.page) !== 1) {
        this.setTablePage(1);
      }
    },
  },
  mounted() {
    this.resumeCsvExportIfNeeded();
  },
  methods: {
    ...mapActions("dashboard", ["searchTransactions"]),
    ...mapMutations("dashboard", [
      "setTransactionsTransactions",
      "setTransactionsLoading",
      "setTransactionsLastEvaluatedKey",
    ]),
    clearResults() {
      this.continueSearchCancel = true;
      this.continueSearchActive = false;
      this.continueSearchBatches = 0;
      this.selectedTransaction = [];
      this.setTransactionsTransactions([]);
      this.setTransactionsLastEvaluatedKey(null);
      this.setTablePage(1);
    },
    setTablePage(page) {
      if ((this.tableOptions && this.tableOptions.page) === page) return;
      this.suppressPageWatch = true;
      // Mutate in place — replacing the whole options object fights
      // v-data-table options.sync and can spin the UI.
      this.$set(this.tableOptions, "page", page);
      this.$nextTick(() => {
        this.suppressPageWatch = false;
      });
    },
    clampToLastLoadedPage() {
      const per = this.tableItemsPerPage;
      const n = this.formattedTransactions.length;
      if (!per) {
        this.setTablePage(1);
        return;
      }
      const lastPage = Math.max(1, Math.ceil(n / per) || 1);
      this.setTablePage(lastPage);
    },
    cancelContinueSearch() {
      if (!this.continueSearchActive) return;
      this.continueSearchCancel = true;
      iziToast.info({
        title: "Transactions",
        message: "Cancelling… will stop after the current batch",
        position: "topRight",
        timeout: 2500,
      });
    },
    onTablePageChange() {
      if (this.suppressPageWatch || this.tableBusy) return;
      // All mode uses Load all / cancel — not page-based fetch
      if (this.isAllRowsMode) return;
      const per = this.tableItemsPerPage;
      const page = (this.tableOptions && this.tableOptions.page) || 1;
      const n = this.formattedTransactions.length;
      const start = (page - 1) * per;
      // Still within loaded rows — nothing to fetch.
      if (start < n) return;
      if (this.hasMoreTransactions) {
        // Keep current rows visible; loop fills target page or exhausts scan.
        this.clampToLastLoadedPage();
        this.handleLoadMore({ targetPage: page });
        return;
      }
      this.clampToLastLoadedPage();
    },
    handleCleared() {
      this.clearResults();
    },
    asOne(val) {
      if (!val || val === "notSet") return null;
      if (Array.isArray(val)) return val.filter(Boolean)[0] || null;
      return val;
    },
    validateFilters() {
      const tid = (this.transactionId || "").trim();
      const did = (this.deviceId || "").trim();

      if (tid) {
        return { ok: true, mode: "c" };
      }
      if (did) {
        const hasStan = !!(this.stan || "").trim();
        const hasAmount =
          this.totalAmount !== null &&
          this.totalAmount !== undefined &&
          String(this.totalAmount).trim() !== "";
        if (!hasStan && !hasAmount) {
          return {
            ok: false,
            message:
              "Mode B requires Device ID plus STAN and/or Total Amount",
          };
        }
        return { ok: true, mode: "b" };
      }

      const sh = this.asOne(this.searchStallholders);
      const stall = this.asOne(this.searchStalls);
      const terminal = this.asOne(this.searchTerminals);
      if (!sh || !stall || !terminal) {
        return {
          ok: false,
          message:
            "Mode A requires From/To dates plus one Stallholder, one Stall, and one Terminal",
        };
      }
      if (
        !this.searchStartTime ||
        this.searchStartTime === "notSet" ||
        !this.searchEndTime ||
        this.searchEndTime === "notSet"
      ) {
        return {
          ok: false,
          message: "Mode A requires From and To date/times",
        };
      }
      return { ok: true, mode: "a" };
    },
    toastSearchResult(result) {
      const total = (result && result.rows && result.rows.length) || 0;
      const hasMore = !!(result && result.hasMore);
      const message = hasMore
        ? `Found ${total} so far — next page continues search`
        : `Found ${total} transaction${total === 1 ? "" : "s"}`;
      iziToast.success({
        title: "Transactions",
        message,
        position: "topRight",
      });
    },
    async handleSubmit() {
      const check = this.validateFilters();
      if (!check.ok) {
        iziToast.warning({
          title: "Transactions",
          message: check.message,
          position: "topRight",
        });
        return;
      }
      this.continueSearchCancel = true;
      this.continueSearchActive = false;
      this.continueSearchBatches = 0;
      this.selectedTransaction = [];
      this.setTransactionsTransactions([]);
      this.setTransactionsLastEvaluatedKey(null);
      this.setTablePage(1);
      try {
        // Submit always loads every remaining batch (Cancel stops early)
        await this.runContinueSearch({
          targetPage: 1,
          seedFirstPage: true,
          loadAll: true,
        });
      } catch (err) {
        iziToast.warning({
          title: "Transactions",
          message: String(
            err?.response?.data?.error ||
              err?.message ||
              "Search failed. Please try again."
          ),
          position: "topRight",
        });
      }
    },
    async handleLoadMore({ targetPage } = {}) {
      if (this.continueSearchActive || !this.hasMoreTransactions) return;
      // All is display-only for rows already in memory
      if (this.isAllRowsMode) return;
      const per = this.tableItemsPerPage;
      const n = this.formattedTransactions.length;
      const pageToFill = targetPage || Math.floor(n / per) + 1;
      try {
        await this.runContinueSearch({ targetPage: pageToFill });
      } catch (err) {
        iziToast.warning({
          title: "Transactions",
          message: String(
            err?.response?.data?.error ||
              err?.message ||
              "Could not load more transactions."
          ),
          position: "topRight",
        });
      }
    },
    async runContinueSearch({
      targetPage = 1,
      seedFirstPage = false,
      loadAll = false,
    } = {}) {
      if (this.continueSearchActive) return;

      const startedWith = seedFirstPage
        ? 0
        : this.formattedTransactions.length;

      this.continueSearchCancel = false;
      this.continueSearchActive = true;
      this.continueSearchBatches = 0;

      try {
        if (seedFirstPage) {
          this.continueSearchBatches = 1;
          await this.searchTransactions({ manageLoading: false });
          if (loadAll && this.hasMoreTransactions) {
            iziToast.info({
              title: "Transactions",
              message: `Found ${this.formattedTransactions.length} so far — loading all… (Cancel to stop)`,
              position: "topRight",
              timeout: 4000,
            });
          } else if (!loadAll) {
            this.toastSearchResult({
              rows: this.formattedTransactions,
              hasMore: this.hasMoreTransactions,
            });
            this.setTablePage(1);
            return;
          } else {
            this.toastSearchResult({
              rows: this.formattedTransactions,
              hasMore: false,
            });
            this.setTablePage(1);
            return;
          }
        }

        while (!this.continueSearchCancel) {
          if (loadAll) {
            if (!this.hasMoreTransactions) break;
            this.continueSearchBatches += 1;
            await this.searchTransactions({
              append: true,
              manageLoading: false,
            });
            continue;
          }

          const per = this.tableItemsPerPage;
          const total = this.formattedTransactions.length;
          const start = (targetPage - 1) * per;
          const pageFilled = start < total;

          if (pageFilled) {
            this.setTablePage(targetPage);
            const added = total - startedWith;
            iziToast.success({
              title: "Transactions",
              message: this.hasMoreTransactions
                ? `Added ${added} (showing ${total}) — next page continues search`
                : `Added ${added} (showing ${total}) — end of results`,
              position: "topRight",
            });
            return;
          }

          if (!this.hasMoreTransactions) break;

          this.continueSearchBatches += 1;
          await this.searchTransactions({
            append: true,
            manageLoading: false,
          });
        }

        if (loadAll) {
          this.setTablePage(1);
        } else {
          this.clampToLastLoadedPage();
        }

        if (this.continueSearchCancel) {
          iziToast.info({
            title: "Transactions",
            message: loadAll
              ? `Stopped — showing ${this.formattedTransactions.length}. Submit again to load the rest.`
              : `Search paused — showing ${this.formattedTransactions.length}. Next page continues.`,
            position: "topRight",
            timeout: 4000,
          });
          return;
        }
        iziToast.success({
          title: "Transactions",
          message:
            this.formattedTransactions.length > 0
              ? `Showing ${this.formattedTransactions.length} — end of results`
              : "No matching transactions",
          position: "topRight",
        });
      } catch (err) {
        this.clampToLastLoadedPage();
        throw err;
      } finally {
        this.continueSearchActive = false;
        this.continueSearchBatches = 0;
        this.continueSearchCancel = false;
      }
    },
    formatTransactionId(id) {
      if (!id) return "";
      const s = String(id);
      return s.length > 12 ? s.slice(-12) : s;
    },
    formatAmount(value) {
      if (value === undefined || value === null || value === "") return "";
      const num = Number(String(value).replace(/[^0-9.-]/g, ""));
      if (Number.isNaN(num)) return String(value);
      return (
        "$" +
        new Intl.NumberFormat("en-AU", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(num)
      );
    },
    lookupTerminalUserName(stallUserId) {
      if (!stallUserId) return "";
      const users = this.allTerminalUsers || [];
      const user = users.find((u) => u.TerminalUserId === stallUserId);
      return user?.TerminalUserName || "";
    },
    lookupVoucherName(transaction) {
      const type = String(transaction.Type || transaction.TransactionType || "")
        .toLowerCase()
        .trim();
      if (type === "card" || type === "cash" || !transaction.VoucherProductId) {
        return "";
      }
      const vouchers = this.allVouchers || [];
      const v = vouchers.find(
        (x) => x.VoucherId === transaction.VoucherProductId
      );
      return v?.VoucherName || "";
    },
    lookupAbn(transaction) {
      const stallId = transaction.StallId;
      if (stallId && this.allStalls?.length) {
        const stall = this.allStalls.find((s) => s.StallId === stallId);
        if (stall?.Abn) return stall.Abn;
      }
      const shId = transaction.StallholderId;
      if (shId && this.allStallHolders?.length) {
        const sh = this.allStallHolders.find((s) => s.StallholderId === shId);
        if (sh?.Abn) return sh.Abn;
      }
      return "";
    },
    csvCell(transaction, col) {
      let value = transaction[col.value];
      if (col.format === "txnId") {
        value = this.formatTransactionId(value);
      } else if (col.format === "money") {
        value = this.formatAmount(value);
      } else if (col.format === "json") {
        if (value === undefined || value === null || value === "") {
          value = "";
        } else if (typeof value === "string") {
          // already serialized
        } else {
          try {
            value = JSON.stringify(value);
          } catch (e) {
            value = "";
          }
        }
      }
      if (value === undefined || value === null) value = "";
      if (typeof value === "boolean") value = value ? "true" : "false";
      const str = String(value);
      return str.includes(",") || str.includes('"') || str.includes("\n")
        ? `"${str.replace(/"/g, '""')}"`
        : str;
    },
    async createPDF() {
      if (!this.selectedRow) {
        iziToast.warning({
          title: "Warning",
          message: "Please select a transaction",
          position: "topRight",
        });
        return;
      }

      const row = this.selectedRow;
      this.pdfBusy = true;
      try {
        const receipt = await dashboardAPI
          .getTransactionsAPI()
          .getReceiptTransaction({
            eventId: this.globalEventFilter.EventId,
            stallholderId: row.StallholderId,
            transactionId: row.TransactionId,
          });

        const merged = {
          ...row,
          ...receipt,
          EventName:
            receipt.EventName ||
            row.EventName ||
            this.globalEventFilter.EventName,
          StallholderName:
            receipt.StallholderName ||
            receipt.StallHolderName ||
            row.StallholderName,
        };

        await this.$pdfEditor.createTaxInvoice(
          `INVOICE-${merged.TransactionId || row.TransactionId}`,
          merged,
          {
            abn: this.lookupAbn(merged),
            voucherName: this.lookupVoucherName(merged),
          }
        );
      } catch (err) {
        console.error(err);
        iziToast.warning({
          title: "Tax Invoice",
          message:
            err?.response?.data?.error ||
            err?.message ||
            "Could not generate tax invoice",
          position: "topRight",
        });
      } finally {
        this.pdfBusy = false;
      }
    },
    generateCSV() {
      const check = this.validateFilters();
      if (!check.ok) {
        iziToast.warning({
          title: "CSV",
          message: check.message,
          position: "topRight",
        });
        return;
      }
      if (this.csvExportBusy) return;
      this.runAsyncCsvExport(check.mode);
    },
    buildExportQuery(mode) {
      const eventId = this.globalEventFilter && this.globalEventFilter.EventId;
      const query = {
        eventId,
        filterMode: mode,
      };
      if (mode === "a") {
        query.stallholders = this.asOne(this.searchStallholders);
        query.stalls = this.asOne(this.searchStalls);
        query.terminals = this.asOne(this.searchTerminals);
        if (this.searchStartTime && this.searchStartTime !== "notSet") {
          query.startTime = this.searchStartTime;
        }
        if (this.searchEndTime && this.searchEndTime !== "notSet") {
          query.endTime = this.searchEndTime;
        }
      } else if (mode === "b") {
        query.deviceId = (this.deviceId || "").trim();
        if ((this.stan || "").trim()) query.stan = (this.stan || "").trim();
        if (
          this.totalAmount !== null &&
          this.totalAmount !== undefined &&
          String(this.totalAmount).trim() !== ""
        ) {
          query.totalAmount = this.totalAmount;
        }
      } else if (mode === "c") {
        query.transactionId = (this.transactionId || "").trim();
      }
      return query;
    },
    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    csvExportStorageKey() {
      return "transactionsCsvExportJob";
    },
    saveCsvExportJob(job) {
      try {
        sessionStorage.setItem(this.csvExportStorageKey(), JSON.stringify(job));
      } catch (e) {
        /* ignore */
      }
    },
    loadCsvExportJob() {
      try {
        const raw = sessionStorage.getItem(this.csvExportStorageKey());
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    },
    clearCsvExportJob() {
      try {
        sessionStorage.removeItem(this.csvExportStorageKey());
      } catch (e) {
        /* ignore */
      }
    },
    cancelCsvExport() {
      if (!this.csvExportBusy) return;
      this.csvExportCancel = true;
      this.csvExportProgress = "Cancelling…";
      const jobId = this.csvExportJobId;
      if (!jobId) return;
      dashboardAPI
        .getTransactionsAPI()
        .cancelTransactionsExport(jobId)
        .catch((err) => {
          console.error("cancel export failed", err);
        });
    },
    resumeCsvExportIfNeeded() {
      const saved = this.loadCsvExportJob();
      if (!saved || !saved.jobId) return;
      if (this.csvExportBusy) return;
      iziToast.info({
        title: "CSV",
        message: "Resuming export in progress…",
        position: "topRight",
        timeout: 3500,
      });
      this.pollCsvExportJob(saved.jobId, {
        modeLabel: saved.modeLabel || "search",
        startedAt: saved.startedAt || Date.now(),
        resumed: true,
      });
    },
    formatCsvElapsed(startedAt) {
      const sec = Math.max(1, Math.round((Date.now() - startedAt) / 1000));
      if (sec < 60) return `${sec}s`;
      const m = Math.floor(sec / 60);
      const s = sec % 60;
      return `${m}m ${s}s`;
    },
    csvProgressMessage(status, startedAt) {
      const state = (status && status.status) || "PENDING";
      const phase = (status && status.phase) || "";
      const rows =
        status && status.rowsSoFar != null
          ? status.rowsSoFar
          : status && status.rowCount != null
          ? status.rowCount
          : null;
      const elapsed = this.formatCsvElapsed(startedAt);
      if (state === "CANCELLED") return `Cancelled (${elapsed})`;
      if (state === "PENDING") return `Queued… (${elapsed})`;
      if (phase === "building_csv") {
        return `Building CSV${
          rows != null ? ` from ${rows.toLocaleString()} rows` : ""
        }… (${elapsed})`;
      }
      if (phase === "uploading") return `Uploading CSV… (${elapsed})`;
      if (rows != null) {
        return `Fetching… ${rows.toLocaleString()} rows so far (${elapsed})`;
      }
      return `Working… (${elapsed})`;
    },
    async runAsyncCsvExport(mode) {
      const modeLabel =
        mode === "b" ? "Mode B" : mode === "c" ? "Mode C" : "Mode A";
      this.csvExportBusy = true;
      this.csvExportCancel = false;
      this.csvExportJobId = null;
      this.csvExportProgress = `Starting ${modeLabel} export…`;
      iziToast.info({
        title: "CSV",
        message: `Exporting full ${modeLabel} results — progress updates while it runs…`,
        position: "topRight",
        timeout: 4000,
      });

      try {
        const api = dashboardAPI.getTransactionsAPI();
        const start = await api.startTransactionsExport({
          eventName: this.globalEventFilter.EventName || "",
          query: this.buildExportQuery(mode),
        });
        const jobId = start && start.jobId;
        if (!jobId) {
          throw new Error("No export job id returned");
        }
        const startedAt = Date.now();
        this.saveCsvExportJob({ jobId, modeLabel, startedAt });
        await this.pollCsvExportJob(jobId, { modeLabel, startedAt });
      } catch (err) {
        if (this.csvExportCancel) {
          iziToast.info({
            title: "CSV",
            message: "Export cancelled",
            position: "topRight",
          });
          return;
        }
        console.error(err);
        this.clearCsvExportJob();
        this.csvExportBusy = false;
        this.csvExportProgress = "";
        this.csvExportJobId = null;
        this.csvExportCancel = false;
        iziToast.warning({
          title: "CSV",
          message: String(
            err?.response?.data?.error ||
              err?.message ||
              "Could not generate CSV report"
          ),
          position: "topRight",
        });
      }
    },
    async pollCsvExportJob(jobId, { modeLabel, startedAt, resumed } = {}) {
      this.csvExportBusy = true;
      this.csvExportCancel = false;
      this.csvExportJobId = jobId;
      this.csvExportProgress = resumed
        ? `Resuming export… (${this.formatCsvElapsed(startedAt || Date.now())})`
        : this.csvExportProgress || `Working…`;

      const api = dashboardAPI.getTransactionsAPI();
      let lastToastAt = 0;
      const startTs = startedAt || Date.now();

      try {
        if (this.csvExportCancel) {
          await api.cancelTransactionsExport(jobId).catch(() => {});
          iziToast.info({
            title: "CSV",
            message: "Export cancelled",
            position: "topRight",
          });
          return;
        }

        const maxAttempts = 150; // ~5 min at 2s
        for (let i = 0; i < maxAttempts; i++) {
          if (this.csvExportCancel) {
            await api.cancelTransactionsExport(jobId).catch(() => {});
            iziToast.info({
              title: "CSV",
              message: "Export cancelled",
              position: "topRight",
            });
            return;
          }
          // First tick immediately when resuming so we don't wait 2s for status
          if (!(resumed && i === 0)) {
            await this.sleep(2000);
          }
          const status = await api.getTransactionsExportStatus(jobId);
          const state = (status && status.status) || "";
          const msg = this.csvProgressMessage(status, startTs);
          this.csvExportProgress = msg;

          if (state === "CANCELLED") {
            iziToast.info({
              title: "CSV",
              message: "Export cancelled",
              position: "topRight",
            });
            return;
          }

          const now = Date.now();
          if (now - lastToastAt >= 10000 && state !== "COMPLETE") {
            lastToastAt = now;
            iziToast.info({
              title: "CSV",
              message: msg,
              position: "topRight",
              timeout: 3500,
            });
          }

          if (state === "COMPLETE") {
            if (this.csvExportCancel) {
              iziToast.info({
                title: "CSV",
                message:
                  "Export cancelled (file may still have finished on server)",
                position: "topRight",
              });
              return;
            }
            if (!status.downloadUrl) {
              throw new Error("Export finished but no download URL");
            }
            downloadFromUrl(status.downloadUrl);
            iziToast.success({
              title: "CSV",
              message:
                status.rowCount != null
                  ? `Download started (${status.rowCount.toLocaleString()} rows)`
                  : "Download started",
              position: "topRight",
            });
            return;
          }
          if (state === "FAILED") {
            throw new Error(status.error || "Export failed");
          }
          if (state === "NOT_FOUND" || status?.error === "Job not found") {
            throw new Error("Export job not found — start a new CSV export");
          }
        }
        throw new Error("Export timed out — try again or narrow filters");
      } catch (err) {
        if (this.csvExportCancel) {
          iziToast.info({
            title: "CSV",
            message: "Export cancelled",
            position: "topRight",
          });
          return;
        }
        console.error(err);
        iziToast.warning({
          title: "CSV",
          message: String(
            err?.response?.data?.error ||
              err?.message ||
              "Could not generate CSV report"
          ),
          position: "topRight",
        });
      } finally {
        this.clearCsvExportJob();
        this.csvExportBusy = false;
        this.csvExportProgress = "";
        this.csvExportJobId = null;
        this.csvExportCancel = false;
      }
    },
    async showRawTransaction() {
      if (!this.selectedRow) {
        iziToast.warning({
          title: "Warning",
          message: "Please select a transaction",
          position: "topRight",
        });
        return;
      }
      const row = this.selectedRow;
      try {
        const raw = await dashboardAPI.getTransactionsAPI().getRawTransaction({
          eventId: this.globalEventFilter.EventId,
          stallholderId: row.StallholderId,
          transactionId: row.TransactionId,
        });
        this.rawJsonText = JSON.stringify(raw, null, 2);
        this.rawDialog = true;
      } catch (err) {
        console.error(err);
        iziToast.warning({
          title: "Transaction Data",
          message:
            err?.response?.data?.error ||
            "Could not load raw transaction (admin only)",
          position: "topRight",
        });
      }
    },
    downloadRawTxt() {
      const blob = new Blob([this.rawJsonText], {
        type: "text/plain;charset=utf-8;",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      const id = this.selectedRow?.TransactionId || "transaction";
      link.setAttribute("href", url);
      link.setAttribute("download", `${id}-raw.txt`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
};
</script>

<style scoped>
h1 {
  color: #a09c94;
  font-family: sans-serif;
}
.raw-json {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  max-height: 60vh;
  overflow: auto;
}
</style>
